// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Button, Col, Form, Input, Result, Row, Spin} from "antd";
import * as AuthBackend from "./AuthBackend";
import * as ApplicationBackend from "../backend/ApplicationBackend";
import * as Setting from "../Setting";
import i18next from "i18next";
import {Oauth2PhoneSendCodeInput} from "./Oauth2PhoneSendCodeInput";
import CustomGithubCorner from "../common/CustomGithubCorner";
import {withRouter} from "react-router-dom";
import {ArrowLeftOutlined} from "@ant-design/icons";
import RedirectForm from "../common/RedirectForm";
import useTrackSend from "../component/Tracker/alayaTracker";

class CompletePhonePage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      classes: props,
      applicationName: props.applicationName ?? props.match?.params?.applicationName,
      application: null,
      msg: null,
      phone: "",
      phoneCode: "",
      body: null,
      oAuthParams: null,
      signinUrl: "",
      loading: false,
      phoneExists: false,
      phoneCheckMsg: "",
      samlResponse: "",
      relayState: "",
      redirectUrl: "",
      redirectUri: "",
    };
    this.form = React.createRef();
    this.trackSend = useTrackSend({
      enableMixpanel: true,
      enableUMeng: true,
    });
  }

  componentDidMount() {
    const body = sessionStorage.getItem("completePhone_body");
    const oAuthParams = sessionStorage.getItem("completePhone_oAuthParams");
    const signinUrl = sessionStorage.getItem("completePhone_signinUrl");
    const phone = sessionStorage.getItem("completePhone_phone");
    const redirectUri = sessionStorage.getItem("completePhone_redirectUri");

    // if (!body) {
    //   this.setState({msg: i18next.t("general:Missing required parameters")});
    //   return;
    // }

    const parsedBody = body ? JSON.parse(body) : null;

    this.setState({
      body: parsedBody,
      oAuthParams: oAuthParams ? JSON.parse(oAuthParams) : null,
      signinUrl: signinUrl || "",
      phone: phone || "",
      redirectUri: redirectUri || "",
    }, () => {
      if (phone && Setting.isValidPhone(phone, "CN")) {
        this.checkPhoneExist(phone);
      }
    });

    if (this.getApplicationObj() === undefined || this.getApplicationObj() === null) {
      this.getApplication();
    }
  }

  getApplication() {
    if (!this.state.applicationName) {
      return;
    }

    ApplicationBackend.getApplication("admin", this.state.applicationName)
      .then((res) => {
        if (res.status === "error") {
          Setting.showMessage("error", res.msg);
          return;
        }
        this.onUpdateApplication(res.data);
        this.setState({application: res.data});
      });
  }

  getApplicationObj() {
    return this.props.application || this.state.application;
  }

  onUpdateApplication(application) {
    this.props.onUpdateApplication(application);
  }

  clearSessionData() {
    sessionStorage.removeItem("completePhone_body");
    sessionStorage.removeItem("completePhone_oAuthParams");
    sessionStorage.removeItem("completePhone_signinUrl");
    sessionStorage.removeItem("completePhone_phone");
    sessionStorage.removeItem("completePhone_redirectUri");
  }

  handleLoginResponse(res) {
    const {oAuthParams, signinUrl} = this.state;
    const applicationName = this.state.applicationName;
    const responseType = this.state.body?.type;
    const concatChar = oAuthParams?.redirectUri?.includes("?") ? "&" : "?";

    if (res.status === "ok") {
      this.clearSessionData();

      if (responseType === "login") {
        if (res.data2) {
          sessionStorage.setItem("signinUrl", signinUrl);
          Setting.goToLinkSoft(this, `/forget/${applicationName}`);
          return;
        }
        Setting.showMessage("success", "Logged in successfully");
        const link = this.state.redirectUri || Setting.getFromLink();
        Setting.goToLink(link);
      } else if (responseType === "code") {
        if (res.data2) {
          sessionStorage.setItem("signinUrl", signinUrl);
          Setting.goToLinkSoft(this, `/forget/${applicationName}`);
          return;
        }
        const code = res.data;
        Setting.goToLink(`${oAuthParams.redirectUri}${concatChar}code=${code}&state=${oAuthParams.state}`);
      } else if (responseType === "token" || responseType === "id_token") {
        if (res.data2) {
          sessionStorage.setItem("signinUrl", signinUrl);
          Setting.goToLinkSoft(this, `/forget/${applicationName}`);
          return;
        }
        const token = res.data;
        Setting.goToLink(`${oAuthParams.redirectUri}${concatChar}${responseType}=${token}&state=${oAuthParams.state}&token_type=bearer`);
      } else if (responseType === "link") {
        Setting.goToLinkSoftOrJumpSelf(this, Setting.getFromLink());
      } else if (responseType === "saml") {
        if (res.data2.method === "POST") {
          this.setState({
            samlResponse: res.data,
            redirectUrl: res.data2.redirectUrl,
            relayState: oAuthParams.relayState,
          });
        } else {
          if (res.data2.needUpdatePassword) {
            sessionStorage.setItem("signinUrl", signinUrl);
            Setting.goToLinkSoft(this, `/forget/${applicationName}`);
            return;
          }
          const SAMLResponse = res.data;
          const redirectUri = res.data2.redirectUrl;
          Setting.goToLink(`${redirectUri}?SAMLResponse=${encodeURIComponent(SAMLResponse)}&RelayState=${oAuthParams.relayState}`);
        }
      }
    } else {
      this.setState({loading: false});
      Setting.showMessage("error", `${i18next.t("application:Failed to sign in")}: ${res.msg}`);
    }
  }

  onFinish(values) {
    const {body, oAuthParams} = this.state;
    if (!body) {
      Setting.showMessage("error", i18next.t("general:Missing required parameters"));
      return;
    }

    this.setState({loading: true});

    const loginBody = {
      ...body,
      phone: values.phone,
      phoneCode: values.code,
    };

    AuthBackend.login(loginBody, oAuthParams)
      .then((res) => {
        this.handleLoginResponse(res);
      })
      .catch((error) => {
        this.setState({loading: false});
        Setting.showMessage("error", `${i18next.t("general:Failed to connect to server")}: ${error}`);
      });
  }

  checkPhoneExist(phone) {
    if (!phone || !Setting.isValidPhone(phone, 'CN')) {
      this.setState({phoneExists: false, phoneCheckMsg: ""});
      return;
    }

    const {body} = this.state;
    const checkBody = {
      application: body?.application || "",
      provider: body?.provider || "",
      redirectUri: body?.redirectUri || "",
      code: body?.code || "",
      state: body?.state || "",
      phone: phone,
    };

    AuthBackend.checkOauth2UserPhoneExist(checkBody)
      .then((res) => {
        if (res.status === "ok") {
          const exists = res.data?.exist === true;
          this.setState({
            phoneExists: exists,
            phoneCheckMsg: exists ? i18next.t("signup:Phone already exists") : "",
          });
        }
      })
      .catch(() => {});
  }

  stepBack() {
    this.clearSessionData();
    const {signinUrl} = this.state;
    if (signinUrl) {
      Setting.goToLink(signinUrl);
    } else {
      Setting.redirectToLoginPage(this.getApplicationObj(), this.props.history);
    }
  }

  renderForm(application) {
    const spanStyle = {
      fontWeight: 400,
      fontSize: "16px",
      color: "rgba(0,0,0,0.88)",
      lineHeight: "24px",
      marginBottom: "8px",
    };

    return (
      <Form
        ref={this.form}
        name="completePhone"
        onFinish={(values) => this.onFinish(values)}
        onFinishFailed={(errorInfo) => {
          // eslint-disable-next-line no-console
          console.log(errorInfo);
        }}
        initialValues={{
          phone: this.state.phone,
        }}
        style={{width: "100%"}}
        size="large"
      >
        <div style={{...spanStyle}}>{i18next.t("general:Phone")}</div>
        <Form.Item
          name="phone"
          validateStatus={this.state.phoneExists ? "error" : undefined}
          help={this.state.phoneExists ? this.state.phoneCheckMsg : undefined}
          rules={[
            {
              required: true,
              message: i18next.t("login:Please input your Phone!"),
            },
            {
              validator: (_, value) => {
                if (!value || Setting.isValidPhone(value, 'CN')) {
                  return Promise.resolve();
                }
                return Promise.reject(i18next.t("login:The input is not valid phone number!"));
              },
            },
          ]}
        >
          <Input
            placeholder={i18next.t("login:Please input your Phone!")}
            onChange={(e) => {
              const val = e.target.value;
              this.setState({phone: val, phoneExists: false, phoneCheckMsg: ""});
              this.checkPhoneExist(val);
            }}
          />
        </Form.Item>

        <div style={{...spanStyle}}>{i18next.t("forget:Verification code")}</div>
        <Form.Item
          name="code"
          rules={[
            {
              required: true,
              message: i18next.t("code:Please input your verification code!"),
            },
          ]}
        >
          <Oauth2PhoneSendCodeInput
            disabled={!Setting.isValidPhone(this.state.phone, "CN") || this.state.phoneExists}
            phone={this.state.phone}
            oauthBody={this.state.body}
            onSendCode={() => {
              this.trackSend.trackSend({
                trackEventName: "sso_complete_phone_send_code_click",
              });
            }}
          />
        </Form.Item>

        <br />
        <Form.Item>
          <Button
            block
            type="primary"
            htmlType="submit"
            disabled={!Setting.isValidPhone(this.state.phone, "CN") || this.state.phoneExists}
            loading={this.state.loading}
            onClick={() => {
              this.trackSend.trackSend({
                trackEventName: "sso_complete_phone_submit_click",
              });
            }}
          >
            {i18next.t("code:Submit and complete")}
          </Button>
        </Form.Item>
      </Form>
    );
  }

  renderContent() {
    const application = this.getApplicationObj();

    if (this.state.msg) {
      return (
        <Result
          status="error"
          title={i18next.t("general:There was a problem signing you in..")}
          subTitle={this.state.msg}
          extra={[
            <Button type="primary" key="back" onClick={() => { this.stepBack(); }}>
              {i18next.t("general:Back")}
            </Button>,
          ]}
        />
      );
    }

    // if (!this.state.body) {
    //   return <Spin size="large" style={{display: "block", margin: "80px auto"}} />;
    // }

    if (!application) {
      return <Spin size="large" style={{display: "block", margin: "80px auto"}} />;
    }

    return this.renderForm(application);
  }

  render() {
    if (this.state.samlResponse !== "") {
      return <RedirectForm samlResponse={this.state.samlResponse} redirectUrl={this.state.redirectUrl} relayState={this.state.relayState} />;
    }

    return (
      <React.Fragment>
        <img className="forget-page-box" src={require("../assets/forget_bg.png")} alt="" style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 0,
          opacity: "1",
        }} />
        <CustomGithubCorner />
        <div className="forget-content" style={{padding: Setting.isMobile() ? "0" : null, boxShadow: Setting.isMobile() ? "none" : null, backgroundColor: "#fff", zIndex: "1"}}>
          {!this.state.msg && (
            <Button className="forget-button" icon={<ArrowLeftOutlined />} onClick={() => { this.stepBack(); }}>
              <span>{i18next.t("forget:Return to login")}</span>
            </Button>
          )}
          <Row>
            <Col span={24} style={{justifyContent: "center"}}>
              <Row>
                <Col span={24}>
                  <div style={{textAlign: "center", fontSize: "24px", fontWeight: "bold", color: "rgba(0,0,0,0.88)", marginTop: "20px"}}>
                    {
                      Setting.renderHelmet({
                        organizationObj: {
                          displayName: i18next.t("login:Bind Phone"),
                          favicon: require("../assets/favicon.ico"),
                        },
                      })
                    }
                    {!this.state.msg && i18next.t("login:Bind Phone")}
                  </div>
                </Col>
              </Row>
            </Col>
            <Col span={24} style={{display: "flex", justifyContent: "center"}}>
              <div style={{marginTop: "40px", width: "100%"}}>
                {this.renderContent()}
              </div>
            </Col>
          </Row>
        </div>
      </React.Fragment>
    );
  }
}

export default withRouter(CompletePhonePage);
